<template>
  <layout-default
    :floatingButton="isAllowed"
    @floating-button-action="addNewPatient"
  >
    <template v-slot:toolbar>
      <patients-filters
        :value="queryParams.fullName"
        @search-clear="searchClear"
        @search-on:input="searchInput"
        @search-on:run="searchRun"
      />
      <div class="d-flex aligm-items-center">
        <div v-if="isAllowedToChangePrograms" class="programs_filter">
          <v-select
            v-model="programs"
            :items="availablePrograms"
            :item-text="'name'"
            return-object
            chips
            label="Filter by programs"
            clearable
            multiple
            @input="updateQueryParams"
            placeholder="Select program"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.name }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text text-caption">
                (+{{ programs.length - 1 }} others)
              </span>
            </template>
          </v-select>
        </div>
      </div>
      <v-spacer />
      <v-btn
        v-if="isAllowed && isUserFeatureAvailable"
        color="accent"
        elevation="0"
        rounded
        @click="scheduleScreenersDialog"
      >
        Schedule screeners
      </v-btn>
    </template>
    <div class="patients">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <main-user-table
        :headers="headers"
        :items="patients"
        :items-per-page="queryParams.pageSize"
        :server-items-length="pagination.totalCount || -1"
        :page="queryParams.page"
        :row-component="'participants-list-table-row'"
        @to-user="toPatient"
        @open-simulator="openSimulator"
        @action:get-data="getDataAction"
      />
      <new-practice-patient-dialog
        :journal-available="journalAvailable"
        :smart-survey-available="smartSurveyAvailable"
        :survey-available="surveyAvailable"
        :swap-available="swapAvailable"
        :practice-user-id="getDoctorId()"
        :practice-id="getPracticeId()"
        :show-dialog="showNewPatientDialog"
        :tenant-channel-flags="tenantChannelFlags"
        @close-dialog="closeNewPatientDialog"
        @on-submit="onNewPatientSubmit"
      />
      <schedule-screener-dialog
        :show-dialog="showScheduleScreenersDialog"
        :loading="loading"
        @close-dialog="closeScheduleScreenerDialog"
        @on-submit="scheduleScreeners"
      />
    </div>
  </layout-default>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import { debounce } from '@/helpers/debounce'
import jwt_decode from 'jwt-decode'
import LayoutDefault from '../layouts/LayoutDefault.vue'
import PatientsFilters from '../components/patients/PatientsFilters.vue'
import MainUserTable from '@/components/tables/MainUserTable.vue'
import NewPracticePatientDialog from '@/components/dialogs/NewPracticePatientDialog.vue'
import ScheduleScreenerDialog from '@/components/dialogs/ScheduleScreenerDialog.vue'
import RouterMixin from '@/mixins/router'
import FeaturesSettingsMixin from '@/mixins/featuresSettings'

export default {
  name: 'Patients',
  components: {
    LayoutDefault,
    PatientsFilters,
    MainUserTable,
    NewPracticePatientDialog,
    ScheduleScreenerDialog,
  },
  mixins: [RouterMixin, FeaturesSettingsMixin],
  data() {
    return {
      showNewPatientDialog: false,
      headers: [
        { sortable: false },
        { text: 'Name', value: 'fullName' },
        { text: 'Join date', value: 'creationDate' },
        { text: 'Advisor', value: 'practiceUserFullName' },
        { text: 'Last updated swap', value: 'lastUpdatedSwaps' },
        { text: 'Most recent activity ', value: 'lastActivity' },
        { text: 'Phone no.', value: 'phone', sortable: false },
        { text: 'Channel', value: 'primaryChannel', sortable: false },
        { text: 'Notes', value: 'notes', sortable: false },
      ],
      defaultPatientData: {
        notes: '',
        steps: 0,
        waterGlasses: 0,
      },
      defaultQueryParams: {
        pageSize: 50,
        sortBy: 'fullName',
        sortDirection: 'asc',
      },
      showScheduleScreenersDialog: false,
      journalAvailable: 'Active',
      smartSurveyAvailable: 'Active',
      swapAvailable: 'Active',
      surveyAvailable: 'Inactive',
      // Tenant channel flags resolved from the practice payload. Fields
      // (smsEnabled / emailPromptsEnabled) live on the practice row when the
      // tenant has them set; null/undefined is treated as false (disabled).
      tenantChannelFlags: {
        smsEnabled: false,
        emailPromptsEnabled: false,
      },
    }
  },
  computed: {
    ...mapGetters('userStates', ['getRolename', 'getToken']),
    ...mapState('patientsStates', ['patients', 'loading', 'pagination']),
    ...mapState('userStates', ['user', 'userId', 'userFeatureFlags']),
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState',
    ]),
    ...mapState('programsStates', {
      availablePrograms: 'programs',
      availableProgramsLoading: 'loading',
    }),
    isAllowed() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ==
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryPracticeUserRole[this.getRolename] ==
          this.dictionaryPracticeUserRole.Advisor
      )
    },
    isUserFeatureAvailable() {
      return (
        this.featuresSettings.VUE_FEATURE_SCREENER &&
        this.userFeatureFlags.surveyAvailable !==
          this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ]
      )
    },
    isPracticeAdmin() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
    isAllowedToChangePrograms() {
      return this.isPracticeAdmin || this.isAdvisor
    },
  },
  created() {},
  mounted() {
    const practiceId = this.getPracticeId()
    if (this.isAllowedToChangePrograms && practiceId) {
      this.getPrograms({
        companyId: this.practiceId,
        active: true,
      })
    }
  },
  methods: {
    ...mapActions('patientsStates', ['getPatients', 'addPatient']),
    ...mapActions('practicesStates', ['getRawPractice']),
    ...mapActions('screenersStates', [
      'getScreenerTemplates',
      'scheduleScreenerToParticipants',
    ]),
    ...mapActions('programsStates', ['getPrograms', 'assignProgramsToProfile']),
    toPatient(id) {
      this.$router.push({
        name: 'Patient',
        path: '/participants/:id',
        params: { id: id },
      })
    },
    openSimulator(participantId) {
      this.$router.push({
        name: 'SmsSimulator',
        params: { id: String(participantId) },
      })
    },
    searchClear() {
      this.getDataAction({ ...this.defaultQueryParams })
    },
    searchInput({ key, value }) {
      this.getDataAction({ ...this.defaultQueryParams, [key]: value })
    },
    searchRun(search) {
      this.getDataAction({ ...this.defaultQueryParams, fullName: search })
    },
    updateQueryParams(programs = []) {
      this.searchInput({
        key: 'programsId',
        value: programs.map(el => el.id).join(','),
      })
    },
    getData(data) {
      debounce(
        () => {
          this.getPatients(data)
          this.getFeatureFlagsFromPractice()
        },
        100,
        'getPatients',
      )
    },
    async getFeatureFlagsFromPractice() {
      try {
        const practiceId = this.getPracticeId()
        if (!practiceId) return
        const practice = await this.getRawPractice(practiceId)
        if (practice) {
          const featureFlagState = Object.keys(this.dictionaryFeatureFlagState)
          if (practice.journalAvailable)
            practice.journalAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() ===
                  practice.journalAvailable.toLowerCase(),
              ) || practice.journalAvailable
          if (practice.smartSurveyAvailable)
            practice.smartSurveyAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() ===
                  practice.smartSurveyAvailable.toLowerCase(),
              ) || practice.smartSurveyAvailable
          if (practice.swapAvailable)
            practice.swapAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() ===
                  practice.swapAvailable.toLowerCase(),
              ) || practice.swapAvailable
          if (practice.surveyAvailable)
            practice.surveyAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() ===
                  practice.surveyAvailable.toLowerCase(),
              ) || practice.surveyAvailable
          this.journalAvailable = practice.journalAvailable || 'Active'
          this.swapAvailable = practice.swapAvailable || 'Active'
          this.tenantChannelFlags = {
            smsEnabled: practice.smsEnabled === true,
            emailPromptsEnabled: practice.emailPromptsEnabled === true,
          }
        }
      } catch (err) {
        console.error('Cannot get feature flags settings from practice', {
          err,
        })
        this.journalAvailable = 'Active'
        this.smartSurveyAvailable = 'Active'
        this.swapAvailable = 'Active'
        this.surveyAvailable = 'Inactive'
      }
    },
    getPracticeId() {
      if (this.getToken && this.dictionaryPracticeUserRole[this.getRolename]) {
        const decodedToken = jwt_decode(this.getToken) || {}

        return parseInt(decodedToken.practice_id, 10) || null
      }

      return null
    },
    getDoctorId() {
      if (
        this.getToken &&
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor
      ) {
        const decodedToken = jwt_decode(this.getToken) || {}

        return parseInt(decodedToken.user_id, 10) || null
      }

      return null
    },
    addNewPatient() {
      this.showNewPatientDialog = true
    },
    closeNewPatientDialog() {
      this.showNewPatientDialog = false
    },
    onNewPatientSubmit(patientData) {
      let patient = { ...patientData, ...this.defaultPatientData }
      if (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
        this.dictionaryBhUsersRoleIds.Consultant
      ) {
        patient.consultantId = this.userId
      }
      this.addPatient({ data: patient })
    },
    scheduleScreenersDialog() {
      this.showScheduleScreenersDialog = true
    },
    closeScheduleScreenerDialog() {
      this.showScheduleScreenersDialog = false
    },
    scheduleScreeners(data) {
      this.scheduleScreenerToParticipants(data).then(() => {
        this.closeScheduleScreenerDialog()
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.patients {
  padding: 2 * $gutter;
}

.programs_filter {
  max-width: 250px;
  width: 100%;
  margin-left: 16px;
}
</style>
